import { Box, ButtonBase, Stack, Typography, useMediaQuery } from "@mui/material"
import { alpha, styled, useTheme } from "@mui/material/styles"
import type { FC } from ".."
import { FilmFestivalLogoSimple, GowayLogo, LocationsLogo } from ".."
import StyledContainer from "./StyledContainer"
import filmFestivalGlobals from "./assets/filmFestivalGlobals.json"

const HeroSection: FC = () => {
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down("md"))

  return (
    <Box
      sx={(theme) => ({
        borderBottom: `1px solid ${alpha(theme.palette["neutral.700"].main, 0.2)}`,
      })}
    >
      <StyledContainer maxWidth={1400}>
        <Box
          minHeight="100vh"
          display="flex"
          flexDirection="column"
          position="relative"
          py={{ xs: 6, md: 10 }}
          gap={{ xs: 2, md: 0 }}
        >
          <HeroLogoSection isMobile={isMobile} />
          <Box
            display="grid"
            gridTemplateColumns={{ xs: "1fr", lg: "repeat(3, 1fr)" }}
            alignItems="center"
            columnGap={{ xs: 6, md: 10 }}
            rowGap={12}
            margin={{ xs: "0 0 12px 0", md: "auto" }}
            sx={(theme) => ({
              transition: "all 0.3s ease-in-out",
              padding: { xs: theme.spacing(8), md: theme.spacing(8, 8, 12) },
            })}
          >
            <LeftHandTextSection isMobile={isMobile} />
            <VideoSection />
            <RightHandTextSection />
          </Box>
          <QuoteSection />
        </Box>
      </StyledContainer>
    </Box>
  )
}

export default HeroSection

// A sub component for the hero logo section
const HeroLogoSection: FC<{ isMobile: boolean }> = ({ isMobile }) => {
  return (
    <Box
      display="grid"
      gridTemplateColumns="repeat(3, 1fr)"
      alignItems={{ xs: "center", lg: "flex-start" }}
      justifyItems="center"
      gap={{ xs: 2, lg: 0 }}
      zIndex={1}
    >
      {isMobile ? (
        <>
          <FilmFestivalLogoSimple sx={{ width: 106 }} />
          <GowayLogo sx={{ width: 91 }} />
        </>
      ) : (
        <>
          <Box display="flex" marginRight="auto">
            <GowayLogo sx={{ width: 137 }} />
          </Box>
          <Box
            display="flex"
            alignItems="center"
            justifyContent="center"
            gap={4}
            sx={{ paddingLeft: "20%" }}
          >
            <FilmFestivalLogoSimple sx={{ width: 175, height: 108 }} />
            <LocationsLogo variant="tertiary" sx={{ width: 72, height: 43 }} />
          </Box>
        </>
      )}
    </Box>
  )
}

// A sub component for the left hand text section
const LeftHandTextSection: FC<{ isMobile: boolean }> = ({ isMobile }) => {
  return (
    <Stack textAlign={{ xs: "center", lg: "left" }}>
      {isMobile ? (
        <StyledTypography variant="heading.xl" sx={{ maxWidth: "100%" }}>
          <span>{filmFestivalGlobals.heroSection.leftTitle} </span>
          <span style={{ fontStyle: "italic", fontWeight: 400 }}>
            {filmFestivalGlobals.heroSection.leftTitleKeyword}
          </span>
          <span>, </span>
          <span>{filmFestivalGlobals.heroSection.leftSubtitle} </span>
          <span style={{ fontStyle: "italic", fontWeight: 400 }}>
            {filmFestivalGlobals.heroSection.leftSubtitleKeyword}
          </span>
        </StyledTypography>
      ) : (
        <StyledTypography
          variant="heading.xl"
          sx={{ maxWidth: { xs: "100%", lg: "400px" } }}
          display="flex"
          flexDirection="column"
        >
          <span>{filmFestivalGlobals.heroSection.leftTitle} </span>
          <span style={{ fontStyle: "italic", fontWeight: 400, paddingBottom: 12 }}>
            {filmFestivalGlobals.heroSection.leftTitleKeyword}
          </span>
          <span>{filmFestivalGlobals.heroSection.leftSubtitle} </span>
          <span style={{ fontStyle: "italic", fontWeight: 400 }}>
            {filmFestivalGlobals.heroSection.leftSubtitleKeyword}
          </span>
        </StyledTypography>
      )}
    </Stack>
  )
}

const RightHandTextSection: FC = () => {
  return (
    <Stack
      textAlign={{ xs: "center", lg: "left" }}
      paddingLeft={{ xs: 0, lg: 4 }}
      marginBottom={{ xs: 4, md: 0 }}
    >
      <StyledTypography variant="heading.xl" variation="secondary">
        <span>{filmFestivalGlobals.heroSection.rightTitle} </span>
        <span style={{ fontStyle: "italic", fontWeight: 400 }}>
          {filmFestivalGlobals.heroSection.rightTitleKeyword}
        </span>
      </StyledTypography>
    </Stack>
  )
}

const StyledTypography = styled(Typography)<{ variation?: string }>(
  ({ theme, variation = "primary" }) => ({
    color: theme.palette["primary.800"].main,
    fontWeight: 700,
    fontSize: "72px",
    lineHeight: "110%!important",
    [theme.breakpoints.down("lg")]: {
      fontSize: "32px",
      zIndex: 1,
    },
    ...(variation === "secondary" && {
      fontSize: "56px",
      color: theme.palette["neutral.700"].main,
      lineHeight: "100%!important",
      [theme.breakpoints.down("lg")]: {
        fontSize: "32px",
        zIndex: 1,
      },
    }),
    ...(variation === "quote" && {
      fontSize: "9px",
      lineHeight: "90%!important",
      textTransform: "uppercase",
      fontWeight: 500,
      [theme.breakpoints.down("lg")]: {
        fontSize: "9px",
      },
    }),
  })
)

// A sub component for the quote section
const QuoteSection: FC = () => {
  return (
    <Box
      position="absolute"
      bottom={40}
      left={0}
      right={0}
      display="flex"
      justifyContent={{ xs: "center", md: "space-between" }}
      gap={2}
    >
      <StyledTypography variant="body.md" variation="quote">
        {filmFestivalGlobals.heroSection.quote1}
      </StyledTypography>
      <StyledTypography
        variant="body.md"
        variation="quote"
        sx={{ display: { xs: "none", md: "block" } }}
      >
        {filmFestivalGlobals.heroSection.quote2}
      </StyledTypography>
    </Box>
  )
}

// A sub component for the video section
const VideoSection: FC = () => {
  return (
    <Box
      display="flex"
      justifyContent="center"
      sx={(theme) => ({
        "position": "relative",
        ":before": {
          content: '""',
          position: "absolute",
          top: -150,
          left: -200,
          right: -100,
          bottom: -80,
          background: `url(${filmFestivalGlobals.heroSection.videoBackground}) no-repeat center`,
          backgroundSize: "cover",
          pointerEvents: "none",
          transition: "all 0.2s ease-in-out",
          [theme.breakpoints.down("lg")]: {
            top: -100,
            left: -100,
            right: -100,
            bottom: -70,
            backgroundSize: "contain",
          },
          [theme.breakpoints.down("md")]: {
            bottom: -50,
          },
        },
      })}
    >
      <Box textAlign="center" maxWidth="283px">
        <Box
          sx={{
            "position": "relative",
            "&::before": {
              content: '""',
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              bottom: 6,
              background:
                "linear-gradient(180deg, #F3EAD2 0%, #F05B2A 50%, #FFCB7D 80%, #F3EAD2 100%)",
              opacity: 0.4,
              borderRadius: "24px",
              zIndex: 1,
            },
          }}
        >
          <Box
            component="video"
            src={filmFestivalGlobals.heroSection.video}
            autoPlay
            muted
            loop
            playsInline
            sx={{
              width: { xs: "179px", md: "283px" },
              height: { xs: "348px", md: "523px" },
              objectFit: "cover",
              borderRadius: "24px",
              position: "relative",
              zIndex: 0,
            }}
          >
            <source src={filmFestivalGlobals.heroSection.video} type="video/mp4" />
          </Box>
        </Box>
      </Box>
    </Box>
  )
}
